import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { MediaControlSettingsForm } from '@/components/playout/media-control-settings-form';
import type { ResolvedMediaControlSettings } from '@/types/playout/media-control-settings.types';
import { usePatchFlowMediaControlSettingsMutation } from '@/state/mutations/playout/use-patch-media-control-settings';
import { useGetFlowMediaControlSettingsQuery } from '@/state/queries/playout/use-get-flow-media-control-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { getObjectId } from '@/helpers/object.id.helper';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

export default function FlowPlayoutSettingsPanel({ flow }: { flow: FlowDto }) {
  const t = useTranslations();
  const notification = useNotifications();
  const flowId = flow.origin ? getObjectId(flow.origin) : flow.id;
  const { data, isLoading, isError } = useGetFlowMediaControlSettingsQuery(flowId);
  const { mutateAsync, isPending } = usePatchFlowMediaControlSettingsMutation(flowId);
  const [settings, setSettings] = useState<ResolvedMediaControlSettings | null>(null);

  useEffect(() => {
    if (data) {
      setSettings(data);
    }
  }, [data]);

  if (!settings) {
    if (isLoading) {
      return <CircularProgress size={24} />;
    }
    if (isError) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('playout.mediaControl.loadError', { defaultValue: 'Could not load media control settings' })}
        </Typography>
      );
    }
    return null;
  }

  const handleSave = async () => {
    try {
      const updated = await mutateAsync({ mediaControl: { bindings: settings.bindings } });
      setSettings(updated);
      notification.notifySuccess(
        t('playout.mediaControl.saveSuccess', { defaultValue: 'Media control settings saved' }),
      );
    } catch {
      notification.notifyError(
        t('playout.mediaControl.saveError', { defaultValue: 'Could not save media control settings' }),
      );
    }
  };

  return (
    <Stack spacing={2}>
      <MediaControlSettingsForm
        settings={settings}
        disabled={!settings.canEditAtFlow || isPending}
        onBindingsChange={(bindings) => setSettings((prev) => (prev ? { ...prev, bindings } : prev))}
      />
      <Button variant="contained" onClick={() => void handleSave()} disabled={!settings.canEditAtFlow || isPending}>
        {t('common.save')}
      </Button>
    </Stack>
  );
}
